import { useEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from 'ahooks';
import type { Swiper as SwiperCore } from 'swiper';
import { Carousel, pxToRem, useStyles } from '@archipro-design/aria';

import ImageSlide from './ImageSlide';
import VideoSlide from './VideoSlide';
import * as S from './ProductGallery.style';
import PhotoViewerModalMobile from './ProductViewerModal.mobile';
import { isLibraryVideo } from '@modules/product/util/is-library-video';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import useGalleryVariantSelect from '@modules/product/hook/use-gallery-variant-select';
import getGallerySlides from '~/modules/product/util/get-gallery-slides';
import type {
    ProductDiagramFragmentFragment,
    ProductImageFragmentFragment,
} from 'generated/graphql';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { PreventCloseGesture } from '~/modules/prevent-close-gesture/PreventCloseGesture';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import BannerAwards from '@modules/page-builder/component/detail/banner-awards/BannerAwards';
import { hasFeature, useAppData } from '@modules/root';

interface ProductGalleryProps {
    data: ProductPageLoaderData['product'];
    firstSlide: string;
    title?: string;
}

const getSlideOffset = (slidesLength: number) => {
    const slidesOffsetAfter = 9;
    const spaceBetween = slidesLength > 1 ? 6 : 0;
    const slidesPerView = slidesLength > 1 ? 1.1 : 1;
    const slidesOffsetBefore = slidesLength > 1 ? slidesOffsetAfter : 0;
    return {
        spaceBetween,
        slidesPerView,
        slidesOffsetBefore,
        slidesOffsetAfter,
    };
};

const ProductGalleryMobile = ({
    data,
    firstSlide,
    title,
}: ProductGalleryProps) => {
    const {
        LibraryImages,
        LibraryVideos,
        DiagramImages,
        Title,
        Professional,
        ID,
        Awards,
    } = data;

    const galleryRef = useRef<SwiperCore>();
    const thumbsRef = useRef<SwiperCore>();

    const stack = useMemo(
        () => getGallerySlides({ LibraryImages, LibraryVideos, DiagramImages }),
        [LibraryImages, LibraryVideos, DiagramImages]
    );

    const { slideIndex } = useGalleryVariantSelect(stack);
    const slideIndexRef = useLatest(slideIndex);
    const [modalIndex, setModalIndex] = useState<number>(slideIndex);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [swiperInitialised, setSwiperInitialised] = useState<boolean>(
        !!galleryRef.current
    );

    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const { css } = useStyles({
        swiperInitialised,
    });

    const slides = useMemo(() => {
        return stack.map((slide, idx) => {
            const isVideo = isLibraryVideo(slide);
            return {
                key: `${slide.ID}-ImageSlide`,
                id: slide.ID,
                content: !isVideo ? (
                    <ImageSlideWithFavourite
                        firstSlide={firstSlide}
                        index={idx}
                        setModalOpen={setModalOpen}
                        slide={slide}
                        title={title}
                        productID={ID}
                    />
                ) : (
                    <VideoSlide libraryVideo={slide} />
                ),
            };
        });
    }, [ID, stack, firstSlide, title]);

    useEffect(() => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        gallery.slideTo(slideIndex);
    }, [slideIndex]);

    useEffect(() => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        gallery.slideTo(slideIndexRef.current ?? 0, 0);
    }, [data.ID, slideIndexRef]);

    const handleThumbsTransition = (
        activeIndex: number,
        slideCount: number
    ) => {
        const thumbsSwiper = thumbsRef.current;
        if (!thumbsSwiper) return;

        const THUMB_SIZE = 96.33;
        const THUMB_BOUND_EDGES = 20;
        const LAST_THUMB_GROUP_EDGE = 17;
        const MAX_SLIDE_BOUNDS = slideCount - 4;

        if (activeIndex < 4) {
            thumbsSwiper.translateTo(18, 300);
        } else if (activeIndex > slideCount - 5) {
            thumbsSwiper.translateTo(
                -MAX_SLIDE_BOUNDS * THUMB_SIZE + LAST_THUMB_GROUP_EDGE,
                300
            );
        } else {
            thumbsSwiper.translateTo(
                -activeIndex * THUMB_SIZE + THUMB_BOUND_EDGES,
                300
            );
        }
    };

    galleryRef.current?.on('slideChange', ({ activeIndex, slides }) => {
        setModalIndex(activeIndex);
        handleThumbsTransition(activeIndex, slides.length);
    });

    const {
        slidesOffsetAfter,
        spaceBetween,
        slidesPerView,
        slidesOffsetBefore,
    } = getSlideOffset(slides.length);

    return (
        <PreventCloseGesture>
            <div className={css(S.GalleryWrapper)}>
                <Carousel
                    items={slides}
                    slidesPerView={slidesPerView}
                    spaceBetween={spaceBetween}
                    slidesOffsetBefore={slidesOffsetBefore}
                    slidesOffsetAfter={slidesOffsetAfter}
                    getSwiperApi={(swiper) => {
                        galleryRef.current = swiper as SwiperCore;
                        setSwiperInitialised(true);
                    }}
                    freeMode={true}
                    thumbs={{ swiper: thumbsRef?.current }}
                    hidePaddles
                    className={css(S.Carousel)}
                    variables={{
                        containerWidth: pxToRem(414),
                        containerHeight: pxToRem(378),
                        overlayInitialOpacity: 1,
                    }}
                />

                {enableAwardBadges && Awards && Awards.length > 0 && (
                    <BannerAwards
                        awards={Awards}
                        heading={'Award winning product'}
                    />
                )}

                {modalOpen && (
                    <PhotoViewerModalMobile
                        stack={stack}
                        productTitle={Title}
                        professional={Professional}
                        selectedSlide={modalIndex}
                        open={modalOpen}
                        toggle={() => setModalOpen((curr) => !curr)}
                    />
                )}
            </div>
        </PreventCloseGesture>
    );
};

const ImageSlideWithFavourite = ({
    slide,
    title,
    // setModalOpen,
    firstSlide,
    index,
    productID,
}: {
    slide: ProductImageFragmentFragment | ProductDiagramFragmentFragment;
    title: string | undefined;
    setModalOpen: (open: boolean) => void;
    firstSlide: string;
    index: number;
    productID: number;
}) => {
    const { css } = useStyles();
    const favouriteItem = useFavouriteVariant(productID);

    return (
        <div className="relative w-fit mx-auto">
            <SaveToDesignBoard
                {...favouriteItem}
                heartButton={{
                    className: css(S.FavouriteButton),
                    size: 24,
                    color: 'primary',
                    variables: {
                        buttonPadding: pxToRem(0),
                        buttonRadius: '50%',
                    },
                }}
            />

            <ImageSlide
                libraryImage={
                    index === 0
                        ? firstSlide
                        : slide.LibraryImageFile?.DetailPageSquare.Mobile ?? ''
                }
                isMultiplied={slide.IsMultiplied}
                // onClick={() => setModalOpen(true)} // Kieran disabled this to try improve conversion rates. We can try re-enabling this after October 2024 as long as we first add save buttons to the modal photos
                height={378}
                width={378}
                loadingValue={index === 0 ? 'eager' : 'lazy'}
                sizes="100vw"
                alt={slide.AltText || title}
            />
        </div>
    );
};

export default ProductGalleryMobile;
